import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useEffect } from 'react';
import { useFormik } from 'formik';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { omit } from 'lodash';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  createServiceProduct,
  getServiceProduct,
  patchServiceProduct,
} from '../../../store/serviceProduct/api';
import { findServiceVendor } from '../../../store/serviceVendor/api';
import { findServiceCategory } from '../../../store/serviceCategory/api';
import { decryptString, getAssetURl } from '../../../utils/url';
import serviceProductSchema from '../../../validation/serviceProductSchema';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';
import { useRemountFileInput } from '../../../hooks/useRemountFileInput';
import { toast } from 'react-toastify';

const emptyVariation = () => ({
  labelEn: '',
  labelAr: '',
  descriptionEn: '',
  descriptionAr: '',
  priceDelta: '0',
  durationDays: '1',
  position: '1',
  activeState: true,
});

const ServiceProductForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params: any = useParams();
  const isAddMode = !params?.id;
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector((state) => state.active.selectedItem.assetUrl);
  const { data: vendors, isLoading: isVendorLoading } = useAppSelector(
    (state) => state.serviceVendor
  );
  const { data: categories, isLoading: isCategoryLoading } = useAppSelector(
    (state) => state.serviceCategory
  );
  const hasVendors = (vendors || []).length > 0;
  const hasCategories = (categories || []).length > 0;

  useEffect(() => {
    dispatch(findServiceVendor({ limit: 1000, skip: 0 }));
    dispatch(findServiceCategory({ limit: 1000, skip: 0 }));
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      descriptionEn: '',
      descriptionAr: '',
      images: [] as string[],
      basePrice: '0',
      orderAvailabilityGapDays: '0',
      maxQty: '5',
      dailyCapacity: '',
      position: '1',
      activeState: true,
      categoryId: '',
      vendorId: '',
      variations: [emptyVariation()],
    },
    validationSchema: serviceProductSchema,
    onSubmit: async (values) => {
      const payload: any = {
        ...omit(values, ['id']),
        basePrice: Number(values.basePrice || 0),
        orderAvailabilityGapDays: Number(values.orderAvailabilityGapDays || 0),
        maxQty: Number(values.maxQty || 1),
        dailyCapacity: values.dailyCapacity ? Number(values.dailyCapacity) : null,
        position: Number(values.position || 1),
        variations: (values.variations || []).map((v, idx) => ({
          ...v,
          priceDelta: Number(v.priceDelta || 0),
          durationDays: Math.max(1, Math.min(30, Number(v.durationDays || 1))),
          position: Number(v.position || idx + 1),
        })),
      };
      try {
        if (isAddMode) {
          await dispatch(createServiceProduct(payload)).unwrap();
          toast.success('Service product created');
        } else {
          await dispatch(patchServiceProduct({ id: values.id, ...payload })).unwrap();
          toast.success('Service product updated');
        }
        navigate('/event-service-product');
      } catch {
        /* toast handled */
      }
    },
  });

  useEffect(() => {
    const load = async () => {
      if (isAddMode) return;
      const id = decryptString(params.id);
      const record: any = await dispatch(getServiceProduct({ id })).unwrap();
      formik.setValues({
        id: record.id,
        labelEn: record.labelEn || '',
        labelAr: record.labelAr || '',
        descriptionEn: record.descriptionEn || '',
        descriptionAr: record.descriptionAr || '',
        images: record.images || [],
        basePrice: String(record.basePrice ?? 0),
        orderAvailabilityGapDays: String(record.orderAvailabilityGapDays ?? 0),
        maxQty: String(record.maxQty ?? 1),
        dailyCapacity: record.dailyCapacity == null ? '' : String(record.dailyCapacity),
        position: String(record.position ?? 1),
        activeState: record.activeState !== false,
        categoryId: record.categoryId || '',
        vendorId: record.vendorId || '',
        variations: (record.variations || []).length
          ? record.variations.map((v: any) => ({
              id: v.id,
              labelEn: v.labelEn,
              labelAr: v.labelAr,
              descriptionEn: v.descriptionEn || '',
              descriptionAr: v.descriptionAr || '',
              priceDelta: String(v.priceDelta ?? 0),
              durationDays: String(v.durationDays ?? 1),
              position: String(v.position ?? 1),
              activeState: v.activeState !== false,
            }))
          : [emptyVariation()],
      });
    };
    load();
  }, [isAddMode, params?.id]);

  const handleImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files[0],
        DocumentType.ServiceProduct,
        accessToken
      );
      if (uploadResult?.data?.keypath) {
        formik.setFieldValue('images', [
          ...formik.values.images,
          uploadResult.data.keypath,
        ]);
      }
    }
  };

  const { values, handleChange, handleSubmit, errors, touched, isSubmitting, setFieldValue } =
    formik;
  const canAddPackage = (values.variations || []).every(
    (variation) =>
      String(variation.labelEn || '').trim() &&
      String(variation.labelAr || '').trim()
  );
  const { fileInputRef, fileInputKey, clearFileInput } = useRemountFileInput(
    true,
    values.id || 'new',
    values.images.length > 0,
    'serviceProductImage'
  );

  return (
    <Page title={isAddMode ? 'New event service product' : 'Edit event service product'}>
      <Container maxWidth='xl'>
        <HeaderBreadcrumbs
          heading={isAddMode ? 'New Product' : 'Edit Product'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Products', href: '/event-service-product' },
          ]}
        />
        <form onSubmit={handleSubmit}>
          <Card sx={{ p: 3 }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              General Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Label English'
                  name='labelEn'
                  value={values.labelEn}
                  onChange={handleChange}
                  error={Boolean(errors.labelEn && touched.labelEn)}
                  helperText={Boolean(errors.labelEn && touched.labelEn) && (errors.labelEn as string)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Label Arabic'
                  name='labelAr'
                  value={values.labelAr}
                  onChange={handleChange}
                  error={Boolean(errors.labelAr && touched.labelAr)}
                  helperText={Boolean(errors.labelAr && touched.labelAr) && (errors.labelAr as string)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label='English Description'
                  name='descriptionEn'
                  placeholder='Describe this service...'
                  value={values.descriptionEn}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label='Arabic Description'
                  name='descriptionAr'
                  value={values.descriptionAr}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  disabled={isVendorLoading || !hasVendors}
                  error={Boolean(errors.vendorId && touched.vendorId) && hasVendors}
                >
                  <InputLabel
                    shrink={
                      Boolean(values.vendorId) || !hasVendors || isVendorLoading || undefined
                    }
                  >
                    Select Vendor
                  </InputLabel>
                  <Select
                    label='Select Vendor'
                    name='vendorId'
                    value={values.vendorId}
                    onChange={handleChange}
                    displayEmpty={!hasVendors || isVendorLoading}
                    notched={
                      Boolean(values.vendorId) || !hasVendors || isVendorLoading || undefined
                    }
                    renderValue={(selected) => {
                      if (selected) {
                        return vendors.find((vendor) => vendor.id === selected)?.name || selected;
                      }
                      if (isVendorLoading) return 'Loading...';
                      if (!hasVendors) return 'Not available';
                      return '';
                    }}
                  >
                    {!hasVendors ? (
                      <MenuItem disabled value=''>
                        {isVendorLoading ? 'Loading...' : 'Not available'}
                      </MenuItem>
                    ) : (
                      vendors.map((vendor) => (
                        <MenuItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {Boolean(errors.vendorId && touched.vendorId) && (
                    <FormHelperText>{errors.vendorId as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  disabled={isCategoryLoading || !hasCategories}
                  error={Boolean(errors.categoryId && touched.categoryId) && hasCategories}
                >
                  <InputLabel
                    shrink={
                      Boolean(values.categoryId) ||
                      !hasCategories ||
                      isCategoryLoading ||
                      undefined
                    }
                  >
                    Select Category
                  </InputLabel>
                  <Select
                    label='Select Category'
                    name='categoryId'
                    value={values.categoryId}
                    onChange={handleChange}
                    displayEmpty={!hasCategories || isCategoryLoading}
                    notched={
                      Boolean(values.categoryId) ||
                      !hasCategories ||
                      isCategoryLoading ||
                      undefined
                    }
                    renderValue={(selected) => {
                      if (selected) {
                        return (
                          categories.find((category) => category.id === selected)?.labelEn ||
                          selected
                        );
                      }
                      if (isCategoryLoading) return 'Loading...';
                      if (!hasCategories) return 'Not available';
                      return '';
                    }}
                  >
                    {!hasCategories ? (
                      <MenuItem disabled value=''>
                        {isCategoryLoading ? 'Loading...' : 'Not available'}
                      </MenuItem>
                    ) : (
                      categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.labelEn}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {!isCategoryLoading && !hasCategories ? (
                    <FormHelperText>
                      No category found.{' '}
                      <Link component={RouterLink} to='/event-service-category'>
                        Create category
                      </Link>
                    </FormHelperText>
                  ) : Boolean(errors.categoryId && touched.categoryId) ? (
                    <FormHelperText>{errors.categoryId as string}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Base price (From QAR)'
                  name='basePrice'
                  value={values.basePrice}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Gap days'
                  name='orderAvailabilityGapDays'
                  value={values.orderAvailabilityGapDays}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Max qty'
                  name='maxQty'
                  value={values.maxQty}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Daily capacity (blank = unlimited)'
                  name='dailyCapacity'
                  value={values.dailyCapacity}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <TextField
                    key={fileInputKey}
                    type='file'
                    fullWidth
                    name='images'
                    id='serviceProductImage'
                    inputRef={fileInputRef}
                    onChange={handleImage}
                    inputProps={{ multiple: false, accept: 'image/*' }}
                    InputLabelProps={{ shrink: true }}
                    variant='outlined'
                    label='Image'
                  />
                  {values.images.map((image, index) => (
                    <Box
                      key={image}
                      sx={{
                        position: 'relative',
                        width: { xs: '75px', sm: '100px' },
                        flexShrink: 0,
                        mt: 0.5,
                      }}
                    >
                      <IconButton
                        size='small'
                        aria-label='remove image'
                        onClick={() => {
                          setFieldValue(
                            'images',
                            values.images.filter((_, i) => i !== index)
                          );
                          clearFileInput();
                        }}
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          zIndex: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'grey.100',
                          },
                        }}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                      <Avatar
                        imgProps={{ crossOrigin: 'anonymous' }}
                        src={getAssetURl(image, assetUrl)}
                        sx={{
                          borderRadius: 'unset',
                          objectFit: 'contain',
                          '& img': { objectFit: 'contain' },
                          width: '100%',
                          height: 50,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FormControlLabel
                    sx={{
                      m: 0,
                      alignItems: 'center',
                      '& .MuiCheckbox-root': { p: 0.75 },
                    }}
                    control={
                      <Checkbox
                        checked={values.activeState}
                        onChange={(e) => setFieldValue('activeState', e.target.checked)}
                      />
                    }
                    label='Active Status'
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: 3, mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Packages (variations)
            </Typography>
            {values.variations.map((variation, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  gap: 2,
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <TextField
                  fullWidth
                  label='Label EN'
                  value={variation.labelEn}
                  onChange={(e) =>
                    setFieldValue(`variations[${index}].labelEn`, e.target.value)
                  }
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 180 } }}
                />
                <TextField
                  fullWidth
                  label='Label AR'
                  value={variation.labelAr}
                  onChange={(e) =>
                    setFieldValue(`variations[${index}].labelAr`, e.target.value)
                  }
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 180 } }}
                />
                <TextField
                  fullWidth
                  label='Extra price (QAR)'
                  value={variation.priceDelta}
                  onChange={(e) =>
                    setFieldValue(`variations[${index}].priceDelta`, e.target.value)
                  }
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 140 } }}
                />
                <TextField
                  fullWidth
                  type='number'
                  label='Duration (days)'
                  value={variation.durationDays}
                  inputProps={{ min: 1, max: 30 }}
                  onChange={(e) =>
                    setFieldValue(
                      `variations[${index}].durationDays`,
                      e.target.value
                    )
                  }
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 130 } }}
                />
                <Box
                  sx={{
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    gap: 0.5,
                  }}
                >
                  <FormControlLabel
                    sx={{
                      m: 0,
                      alignItems: 'center',
                      '& .MuiCheckbox-root': { p: 0.75 },
                    }}
                    control={
                      <Checkbox
                        checked={variation.activeState}
                        onChange={(e) =>
                          setFieldValue(
                            `variations[${index}].activeState`,
                            e.target.checked
                          )
                        }
                      />
                    }
                    label='Active'
                  />
                  <IconButton
                    color='error'
                    aria-label='remove package'
                    onClick={() => {
                      const next = values.variations.filter((_, i) => i !== index);
                      setFieldValue(
                        'variations',
                        next.length ? next : [emptyVariation()]
                      );
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Button
              disabled={!canAddPackage}
              onClick={() =>
                setFieldValue('variations', [...values.variations, emptyVariation()])
              }
            >
              Add package
            </Button>
          </Card>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton type='submit' variant='contained' loading={isSubmitting}>
              Save
            </LoadingButton>
          </Box>
        </form>
      </Container>
    </Page>
  );
};

export default ServiceProductForm;
